// Questionnaire "Profil d'apprenant" (12 échelles + 2 questions ouvertes) — 4 langues.
// Le backend renvoie portrait + réglages générés par l'IA ; ces textes servent l'affichage.

export const LEARNER_QUIZ = {
  fr: {
    scale: ["Pas du tout", "Plutôt pas", "Neutre", "Plutôt", "Tout à fait"],
    sections: [
      {
        title: "Relation à l'erreur & à la pression",
        qs: [
          "Quand je fais une erreur, j'ai tendance à être très critique envers moi-même.",
          "Face à une consigne floue ou un problème complexe, je ressens rapidement de la frustration ou du stress.",
          "J'apprends beaucoup mieux quand on me rassure et qu'on valide mes petites réussites étape par étape.",
        ],
      },
      {
        title: "Traitement de l'information",
        qs: [
          "J'ai besoin de comprendre la théorie globale (le pourquoi) avant de passer aux exercices pratiques.",
          "Les longues explications rédigées me fatiguent ; je préfère schémas, exemples concrets et synthèses à puces.",
          "J'aime explorer des idées abstraites et faire des liens par moi-même plutôt qu'appliquer une méthode rigide.",
        ],
      },
      {
        title: "Réflexion & maïeutique",
        qs: [
          "Si je suis bloqué(e), je préfère qu'on me pose une question pour me guider plutôt qu'on me donne la réponse.",
          "J'aime débattre ou remettre en question ce qu'on m'enseigne pour m'assurer que c'est logique.",
          "Je traite mes idées à voix haute ou en écrivant au fur et à mesure, plutôt que tout structurer avant de parler.",
        ],
      },
      {
        title: "Motivation & rythme",
        qs: [
          "J'ai besoin d'un cadre strict, d'objectifs précis et d'un suivi régulier pour rester motivé(e).",
          "Ma concentration fonctionne par vagues : ultra-focus un moment, puis besoin d'une vraie pause.",
          "La curiosité pure me motive plus que les examens ou les notes.",
        ],
      },
    ],
    open1: "Qu'est-ce qui a le plus tendance à vous faire abandonner un apprentissage ?",
    open1ph: "Ex. : l'ennui, le manque de temps, la sensation d'être submergé(e), la difficulté…",
    open2: "Si votre précepteur idéal avait une personnalité, quelle serait sa qualité principale ?",
    open2ph: "Ex. : structuré et exigeant, doux et encourageant, créatif et plein d'humour…",
    ui: {
      cardTitle: "Mon profil d'apprenant",
      cardDesc: "Un court test pour que L'Élan adapte vraiment son approche à ton fonctionnement.",
      start: "Faire le test",
      retake: "Refaire le test",
      submit: "Révéler mon portrait",
      generating: "L'Élan lit entre les lignes…",
      portraitTitle: "Ton portrait d'apprenant",
      settingsTitle: "Réglages de L'Élan pour toi",
      updated: "Mis à jour le",
      cancel: "Annuler",
      lockedTitle: "Réservé aux abonnés",
      lockedDesc: "Le profil d'apprenant, et l'adaptation de L'Élan, font partie de l'expérience abonnée.",
      upsell: "Découvrir les forfaits",
      answerAll: "Merci de répondre à toutes les affirmations.",
      error: "L'analyse n'a pas pu être générée. Réessaie.",
      axes: { pressure: "Sensibilité à la pression", cognitive: "Style cognitif", autonomy: "Autonomie", structure: "Besoin de cadre" },
      settingLabels: { tone: "Ton", verbosity: "Longueur", format: "Format", maieutique: "Maïeutique", cadre: "Cadre", reassurance: "Réassurance" },
    },
  },
  en: {
    scale: ["Not at all", "Rather not", "Neutral", "Rather", "Absolutely"],
    sections: [
      {
        title: "Relationship to error & pressure",
        qs: [
          "When I make a mistake, I tend to be very critical of myself.",
          "Faced with vague instructions or a complex problem, I quickly feel frustration or stress.",
          "I learn much better when I'm reassured and my small wins are validated step by step.",
        ],
      },
      {
        title: "Information processing",
        qs: [
          "I need to grasp the big-picture theory (the why) before moving to practical exercises.",
          "Long written explanations tire me; I prefer diagrams, concrete examples and bullet summaries.",
          "I like exploring abstract ideas and making links myself rather than applying a rigid method.",
        ],
      },
      {
        title: "Thinking & guided questioning",
        qs: [
          "If I'm stuck, I'd rather be asked a question to guide me than be handed the answer.",
          "I like to debate or question what I'm taught to make sure it's logical.",
          "I process ideas out loud or by writing as I go, rather than structuring everything before speaking.",
        ],
      },
      {
        title: "Motivation & pace",
        qs: [
          "I need a strict framework, precise goals and regular follow-up to stay motivated.",
          "My focus comes in waves: ultra-focused for a while, then needing a real break.",
          "Pure curiosity motivates me more than exams or grades.",
        ],
      },
    ],
    open1: "What tends most to make you give up on learning something?",
    open1ph: "e.g. boredom, lack of time, feeling overwhelmed, difficulty…",
    open2: "If your ideal mentor had a personality, what would be their main quality?",
    open2ph: "e.g. structured and demanding, gentle and encouraging, creative and funny…",
    ui: {
      cardTitle: "My learner profile",
      cardDesc: "A short test so L'Élan truly adapts to how your mind works.",
      start: "Take the test",
      retake: "Retake the test",
      submit: "Reveal my portrait",
      generating: "L'Élan is reading between the lines…",
      portraitTitle: "Your learner portrait",
      settingsTitle: "L'Élan's settings for you",
      updated: "Updated on",
      cancel: "Cancel",
      lockedTitle: "Subscribers only",
      lockedDesc: "The learner profile, and L'Élan's adaptation, are part of the subscriber experience.",
      upsell: "See the plans",
      answerAll: "Please answer every statement.",
      error: "The analysis couldn't be generated. Try again.",
      axes: { pressure: "Pressure sensitivity", cognitive: "Cognitive style", autonomy: "Autonomy", structure: "Need for structure" },
      settingLabels: { tone: "Tone", verbosity: "Length", format: "Format", maieutique: "Questioning", cadre: "Framework", reassurance: "Reassurance" },
    },
  },
  es: {
    scale: ["Para nada", "Más bien no", "Neutral", "Más bien sí", "Totalmente"],
    sections: [
      {
        title: "Relación con el error y la presión",
        qs: [
          "Cuando cometo un error, tiendo a ser muy crítico conmigo mismo.",
          "Ante una consigna vaga o un problema complejo, siento rápidamente frustración o estrés.",
          "Aprendo mucho mejor cuando me tranquilizan y validan mis pequeños logros paso a paso.",
        ],
      },
      {
        title: "Procesamiento de la información",
        qs: [
          "Necesito entender la teoría global (el porqué) antes de pasar a los ejercicios prácticos.",
          "Las explicaciones largas me cansan; prefiero esquemas, ejemplos concretos y resúmenes con viñetas.",
          "Me gusta explorar ideas abstractas y hacer conexiones por mí mismo en vez de aplicar un método rígido.",
        ],
      },
      {
        title: "Reflexión y mayéutica",
        qs: [
          "Si me bloqueo, prefiero que me hagan una pregunta que me guíe antes que darme la respuesta.",
          "Me gusta debatir o cuestionar lo que me enseñan para asegurarme de que es lógico.",
          "Proceso mis ideas en voz alta o escribiendo sobre la marcha, en vez de estructurar todo antes de hablar.",
        ],
      },
      {
        title: "Motivación y ritmo",
        qs: [
          "Necesito un marco estricto, objetivos precisos y seguimiento regular para mantenerme motivado.",
          "Mi concentración va por olas: ultra concentrado un rato y luego necesito una pausa real.",
          "La curiosidad pura me motiva más que los exámenes o las notas.",
        ],
      },
    ],
    open1: "¿Qué es lo que más tiende a hacerte abandonar un aprendizaje?",
    open1ph: "Ej.: el aburrimiento, la falta de tiempo, sentirte desbordado, la dificultad…",
    open2: "Si tu preceptor ideal tuviera una personalidad, ¿cuál sería su principal cualidad?",
    open2ph: "Ej.: estructurado y exigente, dulce y alentador, creativo y con humor…",
    ui: {
      cardTitle: "Mi perfil de aprendiz",
      cardDesc: "Un test breve para que L'Élan adapte de verdad su enfoque a tu forma de pensar.",
      start: "Hacer el test",
      retake: "Repetir el test",
      submit: "Revelar mi retrato",
      generating: "L'Élan lee entre líneas…",
      portraitTitle: "Tu retrato de aprendiz",
      settingsTitle: "Ajustes de L'Élan para ti",
      updated: "Actualizado el",
      cancel: "Cancelar",
      lockedTitle: "Solo para suscriptores",
      lockedDesc: "El perfil de aprendiz, y la adaptación de L'Élan, forman parte de la experiencia de suscriptor.",
      upsell: "Ver los planes",
      answerAll: "Por favor, responde a todas las afirmaciones.",
      error: "No se pudo generar el análisis. Inténtalo de nuevo.",
      axes: { pressure: "Sensibilidad a la presión", cognitive: "Estilo cognitivo", autonomy: "Autonomía", structure: "Necesidad de marco" },
      settingLabels: { tone: "Tono", verbosity: "Longitud", format: "Formato", maieutique: "Mayéutica", cadre: "Marco", reassurance: "Tranquilidad" },
    },
  },
  nl: {
    scale: ["Helemaal niet", "Eerder niet", "Neutraal", "Eerder wel", "Helemaal"],
    sections: [
      {
        title: "Omgang met fouten & druk",
        qs: [
          "Als ik een fout maak, ben ik vaak erg kritisch op mezelf.",
          "Bij vage instructies of een complex probleem voel ik snel frustratie of stress.",
          "Ik leer veel beter als ik gerustgesteld word en mijn kleine successen stap voor stap worden bevestigd.",
        ],
      },
      {
        title: "Informatieverwerking",
        qs: [
          "Ik moet de globale theorie (het waarom) begrijpen voordat ik aan praktische oefeningen begin.",
          "Lange geschreven uitleg vermoeit me; ik verkies schema's, concrete voorbeelden en bullet-samenvattingen.",
          "Ik verken graag abstracte ideeën en leg zelf verbanden in plaats van een starre methode te volgen.",
        ],
      },
      {
        title: "Denken & vraaggeleiding",
        qs: [
          "Als ik vastzit, krijg ik liever een vraag die me op weg helpt dan meteen het antwoord.",
          "Ik debatteer of bevraag graag wat me geleerd wordt om zeker te zijn dat het logisch is.",
          "Ik verwerk ideeën hardop of al schrijvend, in plaats van alles vooraf te structureren.",
        ],
      },
      {
        title: "Motivatie & ritme",
        qs: [
          "Ik heb een strak kader, precieze doelen en regelmatige opvolging nodig om gemotiveerd te blijven.",
          "Mijn concentratie komt in golven: een tijd ultra-gefocust, dan een echte pauze nodig.",
          "Pure nieuwsgierigheid motiveert me meer dan examens of cijfers.",
        ],
      },
    ],
    open1: "Wat zorgt er meestal voor dat je stopt met iets leren?",
    open1ph: "Bijv.: verveling, tijdgebrek, het gevoel overweldigd te zijn, moeilijkheid…",
    open2: "Als je ideale mentor een persoonlijkheid had, wat zou dan zijn belangrijkste eigenschap zijn?",
    open2ph: "Bijv.: gestructureerd en veeleisend, zacht en aanmoedigend, creatief en grappig…",
    ui: {
      cardTitle: "Mijn lerprofiel",
      cardDesc: "Een korte test zodat L'Élan zijn aanpak echt afstemt op hoe jouw geest werkt.",
      start: "Doe de test",
      retake: "Test opnieuw",
      submit: "Onthul mijn portret",
      generating: "L'Élan leest tussen de regels…",
      portraitTitle: "Jouw lerportret",
      settingsTitle: "L'Élans instellingen voor jou",
      updated: "Bijgewerkt op",
      cancel: "Annuleren",
      lockedTitle: "Alleen voor abonnees",
      lockedDesc: "Het lerprofiel en de aanpassing van L'Élan horen bij de abonnee-ervaring.",
      upsell: "Bekijk de abonnementen",
      answerAll: "Beantwoord alle stellingen.",
      error: "De analyse kon niet worden gegenereerd. Probeer opnieuw.",
      axes: { pressure: "Gevoeligheid voor druk", cognitive: "Cognitieve stijl", autonomy: "Autonomie", structure: "Behoefte aan kader" },
      settingLabels: { tone: "Toon", verbosity: "Lengte", format: "Format", maieutique: "Vraaggeleiding", cadre: "Kader", reassurance: "Geruststelling" },
    },
  },
};
